import React from 'react';
import { formatTokenCount } from '../../utils/formatters';
import ResultItemCard from './ResultItemCard';

// Result panel for the "Analyse web-site" page: meta badges, the per-item
// cards grid and the estimated-sum footer.
const AnalyzeWebsiteResults = ({
  result,
  modelName,
  resizeMode,
  compressionMode,
  useEbayRag,
  useNotesRag,
  assumeHiddenItems,
  originalPrices,
  originalNames,
  updatingId,
  updatingNameId,
  onPriceChange,
  onManualNameChange,
  onSavePrice,
  onSaveItemName,
}) => {
  const usage = (result && typeof result.usage === 'object') ? result.usage : {};
  const inputTokens = usage.prompt_tokens ?? usage.input_tokens ?? null;
  const outputTokens = usage.completion_tokens ?? usage.output_tokens ?? null;
  const totalTokens = usage.total_tokens ?? (
    Number.isFinite(Number(inputTokens)) && Number.isFinite(Number(outputTokens))
      ? (Number(inputTokens) + Number(outputTokens))
      : null
  );
  const items = Array.isArray(result?.items) ? result.items : [];
  const cacheHit = (
    result?.cache_hit === true ||
    String(result?.analysis_source || '').toLowerCase() === 'cache'
  );
  const analysisSourceLabel = cacheHit ? 'CACHE' : 'LIVE';
  const analysisSourceClass = cacheHit
    ? 'bg-orange-100 text-orange-700 border border-orange-200'
    : 'bg-green-100 text-green-700 border border-green-200';
  const resultUseEbayRag = (
    result?.use_ebay_rag !== undefined
      ? Boolean(result.use_ebay_rag)
      : useEbayRag
  );
  const ragRefs = Math.max(0, Number(result?.ebay_rag_result_count || 0) || 0);
  const resultUseNotesRag = (
    result?.use_notes_rag !== undefined
      ? Boolean(result.use_notes_rag)
      : useNotesRag
  );
  const notesRagRefs = Math.max(0, Number(result?.notes_rag_example_count || 0) || 0);
  const resultAssumeHiddenItems = (
    result?.assume_hidden_items !== undefined
      ? Boolean(result.assume_hidden_items)
      : assumeHiddenItems
  );

  return (
    <div className="mt-6 border border-slate-200 rounded-lg p-4 bg-slate-50">
      <div className="flex flex-wrap gap-2 text-xs mb-3">
        <span className="text-[10px] font-mono text-slate-500 bg-white border px-2 py-0.5 rounded">
          {result.model || modelName}
        </span>
        <span className="text-[10px] font-mono text-emerald-700 bg-emerald-50 border border-emerald-100 px-2 py-0.5 rounded">
          Resize: {result.resize_mode || resizeMode}
        </span>
        <span className="text-[10px] font-mono text-amber-700 bg-amber-50 border border-amber-100 px-2 py-0.5 rounded">
          Compression: {result.compression_mode || compressionMode}
        </span>
        <span className="text-[10px] font-mono text-cyan-700 bg-cyan-50 border border-cyan-100 px-2 py-0.5 rounded">
          eBay RAG: {resultUseEbayRag ? `on (${ragRefs} refs)` : 'off'}
        </span>
        <span className="text-[10px] font-mono text-indigo-700 bg-indigo-50 border border-indigo-100 px-2 py-0.5 rounded">
          Notes RAG: {resultUseNotesRag ? `on (${notesRagRefs} refs)` : 'off'}
        </span>
        <span className="text-[10px] font-mono text-fuchsia-700 bg-fuchsia-50 border border-fuchsia-100 px-2 py-0.5 rounded">
          Hidden Items: {resultAssumeHiddenItems ? 'on' : 'off'}
        </span>
        <span className="text-[10px] font-mono text-blue-600 bg-blue-50 border border-blue-100 px-2 py-0.5 rounded">
          {result.analysis_duration_ms !== undefined && result.analysis_duration_ms !== null
            ? `${Number(result.analysis_duration_ms).toFixed(0)}ms`
            : 'N/A'}
        </span>
        <span className="text-[10px] font-mono text-purple-600 bg-purple-50 border border-purple-100 px-2 py-0.5 rounded">
          {result.analysis_cost_display !== undefined
            ? result.analysis_cost_display
            : (result.analysis_cost !== undefined && result.analysis_cost !== null
              ? `$${Number(result.analysis_cost).toFixed(4)}`
              : 'N/A')}
        </span>
        <span className={`text-[10px] font-mono px-2 py-0.5 rounded ${analysisSourceClass}`}>
          Src: {analysisSourceLabel}
        </span>
        <span className="text-[10px] font-mono text-blue-700 bg-blue-100 border border-blue-200 px-2 py-0.5 rounded">
          In Tok: {formatTokenCount(inputTokens)}
        </span>
        <span className="text-[10px] font-mono text-violet-700 bg-violet-100 border border-violet-200 px-2 py-0.5 rounded">
          Out Tok: {formatTokenCount(outputTokens)}
        </span>
        <span className="text-[10px] font-mono text-emerald-700 bg-emerald-100 border border-emerald-200 px-2 py-0.5 rounded">
          Tot Tok: {formatTokenCount(totalTokens)}
        </span>
        <span className="text-[10px] font-mono text-amber-700 bg-amber-100 border border-amber-200 px-2 py-0.5 rounded">
          Items: {items.length}
        </span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {items.map((item, index) => (
          <ResultItemCard
            key={`${item.item || 'item'}-${index}`}
            item={item}
            index={index}
            originalPrices={originalPrices}
            originalNames={originalNames}
            updatingId={updatingId}
            updatingNameId={updatingNameId}
            onPriceChange={onPriceChange}
            onManualNameChange={onManualNameChange}
            onSavePrice={onSavePrice}
            onSaveItemName={onSaveItemName}
          />
        ))}
        {items.length === 0 ? <div className="text-sm text-slate-500">No items returned.</div> : null}
      </div>
      {items.length >= 1 && (
        <div className="mt-3 p-3 bg-white border border-slate-200 rounded-lg flex justify-between items-center">
          <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Est. Sum</span>
          <span className="text-base text-green-600 font-bold">
            ${items.reduce((sum, i) => sum + (parseFloat(i.estimated_value) || 0), 0).toLocaleString()}
          </span>
        </div>
      )}
    </div>
  );
};

export default AnalyzeWebsiteResults;
